import React from 'react';
import { RouteObject } from 'react-router-dom';

// Main App (with navigation)
import App from '../App';

// Smart Home components
import MorningRoutine from '../components/morning-routine/MorningRoutine';
import IntelligentSecurityMonitor from '../components/security-monitor/IntelligentSecurityMonitor';
import AdaptiveCleaningAutomation from '../components/cleaning-automation/AdaptiveCleaningAutomation';
import PresenceAwareEnergyManagement from '../components/energy-management/PresenceAwareEnergyManagement';

// Advanced Analysis components 
import AdvancedGaitAnalysis from '../components/AdvancedGaitAnalysis';
import FootHealthMonitoring from '../components/FootHealthMonitoring';
import SubtleChangeDetection from '../components/subtle-change/SubtleChangeDetection';
import BathroomSafety from '../components/BathroomSafety';

// Health & Safety components
import FallDetection from '../components/fall-detection/FallDetection';
import EmergencyAlerts from '../components/EmergencyAlerts';
import SafetyAlerts from '../components/SafetyAlerts';

// Wrapper for embeddable views
import EmbeddableView from '../components/EmbeddableView';

// Embeddable Smart Home components
const EmbeddableMorningRoutine = () => (
  <EmbeddableView>
    <MorningRoutine />
  </EmbeddableView>
);

const EmbeddableSecurityMonitor = () => (
  <EmbeddableView>
    <IntelligentSecurityMonitor />
  </EmbeddableView>
);

const EmbeddableCleaningAutomation = () => (
  <EmbeddableView>
    <AdaptiveCleaningAutomation />
  </EmbeddableView>
);

const EmbeddableEnergyManagement = () => (
  <EmbeddableView>
    <PresenceAwareEnergyManagement />
  </EmbeddableView>
);

// Embeddable Advanced Analysis components
const EmbeddableGaitAnalysis = () => (
  <EmbeddableView>
    <AdvancedGaitAnalysis />
  </EmbeddableView>
);

const EmbeddableFootHealth = () => (
  <EmbeddableView>
    <FootHealthMonitoring />
  </EmbeddableView>
);

const EmbeddableSubtleChange = () => (
  <EmbeddableView>
    <SubtleChangeDetection />
  </EmbeddableView>
);

const EmbeddableBathroomSafety = () => (
  <EmbeddableView>
    <BathroomSafety />
  </EmbeddableView>
);

// Embeddable Health & Safety components
const EmbeddableFallDetection = () => (
  <EmbeddableView>
    <FallDetection />
  </EmbeddableView>
);

const EmbeddableEmergencyAlerts = () => (
  <EmbeddableView>
    <EmergencyAlerts alerts={[]} />
  </EmbeddableView>
);

const EmbeddableSafetyAlerts = () => (
  <EmbeddableView>
    <SafetyAlerts />
  </EmbeddableView>
);

// Define routes
export const routes: RouteObject[] = [
  // Main app routes - all paths that should render the main App with navigation
  {
    path: '/',
    element: <App />
  },
  {
    path: '/emergency',
    element: <App />
  },
  {
    path: '/safety',
    element: <App />
  },
  {
    path: '/falls',
    element: <App />
  },
  {
    path: '/advanced-gait',
    element: <App />
  },
  {
    path: '/foot-health',
    element: <App />
  },
  {
    path: '/subtle-change',
    element: <App />
  },
  {
    path: '/bathroom',
    element: <App />
  },
  {
    path: '/morning-routine',
    element: <App />
  },
  {
    path: '/security-monitor',
    element: <App />
  },
  {
    path: '/cleaning-automation',
    element: <App />
  },
  {
    path: '/energy-management',
    element: <App />
  },
  {
    path: '/caregiver',
    element: <App />
  },
  {
    path: '/products',
    element: <App />
  },
  {
    path: '/device',
    element: <App />
  },
  {
    path: '/settings',
    element: <App />
  },
  // Hidden routes
  {
    path: '/recovery',
    element: <App />
  },
  {
    path: '/cognitive',
    element: <App />
  },
  {
    path: '/social',
    element: <App />
  },
  {
    path: '/smarthome',
    element: <App />
  },
  // Other routes that might be present in the navigation
  {
    path: '/home',
    element: <App />
  },
  {
    path: '/activity',
    element: <App />
  },
  {
    path: '/health',
    element: <App />
  },
  {
    path: '/mobility',
    element: <App />
  },
  {
    path: '/sleep',
    element: <App />
  },
  {
    path: '/gait',
    element: <App />
  },
  {
    path: '/night',
    element: <App />
  },
  {
    path: '/routine',
    element: <App />
  },
  {
    path: '/wellness',
    element: <App />
  },
  {
    path: '/location',
    element: <App />
  },
  {
    path: '/physician',
    element: <App />
  },
  
  // Smart Home embedded routes
  {
    path: '/embed/smart-home/morning-routine',
    element: <EmbeddableMorningRoutine />
  },
  {
    path: '/embed/smart-home/security',
    element: <EmbeddableSecurityMonitor />
  },
  {
    path: '/embed/smart-home/cleaning',
    element: <EmbeddableCleaningAutomation />
  },
  {
    path: '/embed/smart-home/energy',
    element: <EmbeddableEnergyManagement />
  },
  
  // Advanced Analysis embedded routes
  {
    path: '/embed/analysis/gait',
    element: <EmbeddableGaitAnalysis />
  },
  {
    path: '/embed/analysis/foot-health',
    element: <EmbeddableFootHealth />
  },
  {
    path: '/embed/analysis/subtle-change',
    element: <EmbeddableSubtleChange />
  },
  {
    path: '/embed/analysis/bathroom-safety',
    element: <EmbeddableBathroomSafety />
  },
  
  // Health & Safety embedded routes
  {
    path: '/embed/safety/fall-detection',
    element: <EmbeddableFallDetection />
  },
  {
    path: '/embed/safety/emergency-alerts',
    element: <EmbeddableEmergencyAlerts />
  },
  {
    path: '/embed/safety/safety-alerts',
    element: <EmbeddableSafetyAlerts />
  },
  
  // Smart home group embedded route (combines all smart home features)
  {
    path: '/embed/smart-home',
    element: (
      <EmbeddableView>
        <div className="space-y-8">
          <MorningRoutine />
          <IntelligentSecurityMonitor />
          <AdaptiveCleaningAutomation />
          <PresenceAwareEnergyManagement />
        </div>
      </EmbeddableView>
    )
  },
  
  // Aging in place group embedded route (combines relevant aging in place features)
  {
    path: '/embed/aging-in-place',
    element: (
      <EmbeddableView>
        <div className="space-y-8">
          <FallDetection />
          <FootHealthMonitoring />
          <SubtleChangeDetection />
          <BathroomSafety />
        </div>
      </EmbeddableView>
    )
  }
]; 